'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { Loader2 } from 'lucide-react';

interface CafeItem {
  id: string;
  name: string;
  area: string;
  imageUrl: string;
  rating: number;
  priceRange: string;
  isOpen: boolean;
  tags: string[];
}

export default function TrendingCafes() {
  const supabase = createClient();
  const [trending, setTrending] = useState<CafeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrending = async () => {
      const { data, error } = await supabase
        .from('coffee_shops')
        .select('id, name, area, image_url, rating, price_range, is_open, tags')
        .gte('rating', 4.0)
        .order('rating', { ascending: false })
        .limit(8);

      if (error) {
        console.log('Trending fetch error:', error.message);
        setLoading(false);
        return;
      }

      setTrending(
        (data || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          area: c.area,
          imageUrl: c.image_url || '',
          rating: c.rating || 0,
          priceRange: c.price_range || 'Rp 20K+',
          isOpen: c.is_open ?? true,
          tags: c.tags || [],
        }))
      );
      setLoading(false);
    };

    fetchTrending();
  }, []);

  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="section-title">🔥 Lagi Hits Sekarang</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Kayaknya kamu bakal suka ini.</p>
          </div>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 size={20} className="animate-spin text-accent" />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="section-title">🔥 Lagi Hits Sekarang</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Kayaknya kamu bakal suka ini.</p>
        </div>
        <Link href="/jelajahi-explore" className="flex items-center gap-1 text-sm font-600 text-accent hover:underline">
          Lihat semua <ChevronRight size={14} />
        </Link>
      </div>

      <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
        {trending.map((cafe) => (
          <Link
            key={`trending-${cafe.id}`}
            href={`/coffeshop-detail-page?id=${cafe.id}`}
            className="flex-shrink-0 w-44 place-card"
          >
            <div className="relative h-28 overflow-hidden">
              <AppImage
                src={cafe.imageUrl}
                alt={`Foto ${cafe.name}, cafe di ${cafe.area} Malang`}
                fill
                className="object-cover"
                sizes="176px"
              />
              <div className="absolute top-2 left-2">
                <span className={cafe.isOpen ? 'open-badge' : 'closed-badge'}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cafe.isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
                  {cafe.isOpen ? 'Buka' : 'Tutup'}
                </span>
              </div>
            </div>
            <div className="p-3">
              <h3 className="text-sm font-700 text-foreground line-clamp-1">{cafe.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{cafe.area}</p>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1">
                  <Star size={11} className="rating-star fill-current" />
                  <span className="text-xs font-700 text-foreground font-tabular">{cafe.rating?.toFixed(1)}</span>
                </div>
                <span className="text-xs text-muted-foreground">{cafe.priceRange?.split(' - ')[0] || 'Rp 20K+'}</span>
              </div>
              <div className="flex gap-1 mt-2 overflow-hidden">
                {cafe.tags?.slice(0, 2)?.map((tag) => (
                  <span key={`tag-${cafe.id}-${tag}`} className="tag-pill text-[10px]">{tag}</span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}